import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// Replaced simple framer-motion wrappers with CSS animations for lighter runtime
import ThemeProvider from './contexts/ThemeContext';
import NotificationProvider from './contexts/NotificationContext';
import WebSocketProvider from './contexts/WebSocketContext';
import EnhancedHeader from './components/EnhancedHeader';
import Navbar from './components/Navbar';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import './App-animations.css';
import apiClient from './api/client';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load heavy components for better initial load performance
const DashboardEnhanced = lazy(() => import('./components/DashboardEnhanced'));
const ProjectAnalyzerEnhanced = lazy(() => import('./components/ProjectAnalyzerEnhanced'));
const Recommendations = lazy(() => import('./components/Recommendations'));
const Settings = lazy(() => import('./components/Settings'));

interface SystemHealth {
  status: string;
  version: string;
  components: Record<string, string>;
  timestamp: string;
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check system health on startup
    const checkHealth = async () => {
      try {
        // Query backend API health (Flask simple_cloud_api exposes /api/v1/health)
        const { data } = await apiClient.get('/api/v1/health');
        // Normalize to SystemHealth shape used by UI
        const normalized: SystemHealth = {
          status: data.status || 'unknown',
          version: data.version || 'unknown',
          components: { backend_api: data.status || 'unknown' },
          timestamp: data.timestamp || new Date().toISOString(),
        };
        setSystemHealth(normalized);
        setError(null);
      } catch (error) {
        console.error('Failed to check system health:', error);
        setError('Failed to connect to backend services');
        // Set fallback health data
        setSystemHealth({
          status: 'degraded',
          version: '2.0.0',
          components: {
            'web_interface': 'operational',
            'backend_api': 'degraded'
          },
          timestamp: new Date().toISOString()
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkHealth();
    
    // Set up periodic health checks
    const healthCheckInterval = setInterval(checkHealth, 60000); // Every minute
    return () => clearInterval(healthCheckInterval);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <NotificationProvider>
          <WebSocketProvider>
            <Router>
              <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
                <EnhancedHeader />
                <Navbar systemHealth={systemHealth} />
              
              {error && (
                <div
                  className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mx-4 mt-4 animate-fade-in-down"
                >
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        {error} - Some features may be limited.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <main
                className="container mx-auto px-4 py-8 animate-fade-in-up"
              >
                <ErrorBoundary>
                  <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                      <Route path="/" element={<DashboardEnhanced />} />
                      <Route path="/analyze" element={<ProjectAnalyzerEnhanced />} />
                      <Route path="/recommendations" element={<Recommendations />} />
                      <Route path="/settings" element={<Settings />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </main>
            </div>
          </Router>
        </WebSocketProvider>
      </NotificationProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;